interface OnboardingStage {
  name: OnboardingStageName;
  status: OnboardingStageStatus;
  value?: OnboardingStageValue;
}

type OnboardingStageValue = string | number | boolean | null | undefined;

export const ONBOARDING_STAGE = {
  welcomePopup: 'WelcomePopup',
  welcomeRetrialPopup: 'WelcomeRetrialPopup',
  commonProjectWalkthrough: 'CommonProjectWalkthrough', // deprecated
  continuousOptimizationWalkthrough: 'ContinuousOptimizationWalkthrough', // deprecated
  productDescriptionWalkthrough: 'ProductDescriptionWalkthrough', // deprecated
  continuousOptimizationTutorial: 'ContinuousOptimizationTutorial', // deprecated
  blogBuilderNewEditingUI: 'BlogBuilderNewEditingUI',
  shopifyWelcome: 'ShopifyWelcome', // deprecated
  briefEditing: 'BriefEditing', // deprecated
  briefEditingBlog: 'BriefEditingBlog', // deprecated
  briefEditingContinuousOptimization: 'BriefEditingContinuousOptimization', // deprecated
  shopifyProductWalkthrough: 'ShopifyProductWalkthrough', // deprecated
  workspaces: 'Workspaces', // deprecated
  dataDrivenDocumentsPopup: 'DataDrivenDocumentsPopup', // deprecated
  basicPlanIntro: 'BasicPlanIntro', // deprecated
  migratedFromStarter: 'MigratedFromStarter',
  confirmAddToCampaign: 'ConfirmAddToCampaign',
  documentVariations: 'DocumentVariations',
  wtmLimitation: 'WtmLimitation',
  accounts: 'Accounts',
  wtmAsset: 'WtmAsset',
  listVariations: 'ListVariations',
  documentSuggestedPersonasNotify: 'MoreSuggestedPersonasNotify',
  documentPromptWithUrl: 'DocumentPromptWithUrl',
  personaBadgeHint: 'personaBadgeHint',
  cip: 'Cip',
  wtmCipMigrationPopup: 'WtmCipMigrationPopup',
  checkoutPlanRoute: 'CheckoutPlanRoute',
  homepageIntegrations: 'HomepageIntegrations',
  homepageTargetAudience: 'HomepageTargetAudience',
  homepageAds: 'HomepageAds',
  homepageEmail: 'HomepageEmail',
  homepageContentImprover: 'HomepageContentImprover',
  homepageLongForm: 'HomepageLongForm',
  homepageSocialPosts: 'HomepageSocialPosts',
  homepageToneOfVoice: 'HomepageToneOfVoice',
  webinarBanner3: 'WebinarBanner3', // deprecated
  webinarBanner2: 'WebinarBanner2', // deprecated
  g2ReviewBoostClaimed: 'G2ReviewBoostClaimed', // deprecated
} as const;
export type OnboardingStageName = (typeof ONBOARDING_STAGE)[keyof typeof ONBOARDING_STAGE];

export const ONBOARDING_STAGE_STATUS = {
  pending: 'pending',
  finished: 'finished',
  skipped: 'skipped',
} as const;
export type OnboardingStageStatus =
  (typeof ONBOARDING_STAGE_STATUS)[keyof typeof ONBOARDING_STAGE_STATUS];

export const ONBOARDING_STEP = {
  workspaces: 'workspaces',
  accounts: 'accounts',

  // Document steps
  documentVariationScore: 'documentVariationScore',
  documentInsightsPanel: 'documentInsightsPanel',
  documentSuggestedPersonasNotify: 'documentSuggestedPersonasNotify',
  documentPromptWithUrl: 'documentPromptWithUrl',

  // List variations
  listVariationActions: 'listVariationActions',

  // Wtm steps
  wtmAssetActions: 'wtmAssetActions',

  // CIP
  cipBoost: 'cipBoost',
} as const;
export type OnboardingStep = (typeof ONBOARDING_STEP)[keyof typeof ONBOARDING_STEP];

const ONBOARDING_STAGE_STEPS: Record<string, OnboardingStep[]> = {
  [ONBOARDING_STAGE.accounts]: [ONBOARDING_STEP.accounts],
  [ONBOARDING_STAGE.listVariations]: [ONBOARDING_STEP.listVariationActions],
  [ONBOARDING_STAGE.documentVariations]: [
    ONBOARDING_STEP.documentVariationScore,
    ONBOARDING_STEP.documentInsightsPanel,
  ],
  [ONBOARDING_STAGE.wtmAsset]: [ONBOARDING_STEP.wtmAssetActions],
  [ONBOARDING_STAGE.documentSuggestedPersonasNotify]: [
    ONBOARDING_STEP.documentSuggestedPersonasNotify,
  ],
  [ONBOARDING_STAGE.cip]: [ONBOARDING_STEP.cipBoost],
};

export const getOnboardingStepsForStage = (stage: OnboardingStageName): OnboardingStep[] => {
  return ONBOARDING_STAGE_STEPS[stage] || [];
};

const getOnboardingStage = (
  stages: OnboardingStage[],
  stageName: OnboardingStageName
): OnboardingStage | undefined => {
  if (!Array.isArray(stages)) {
    return undefined;
  }
  return stages.find((s) => s.name === stageName);
};

export const isOnboardingStagePending = (
  stages: OnboardingStage[],
  stageName: OnboardingStageName
): boolean => {
  const stage = getOnboardingStage(stages, stageName);
  return stage ? stage.status === ONBOARDING_STAGE_STATUS.pending : false;
};

export const isOnboardingStageFinished = (
  stages: OnboardingStage[],
  stageName: OnboardingStageName
): boolean => {
  const stage = getOnboardingStage(stages, stageName);
  return stage ? stage.status === ONBOARDING_STAGE_STATUS.finished : false;
};

export const isOnboardingStageSkipped = (
  stages: OnboardingStage[],
  stageName: OnboardingStageName
): boolean => {
  const stage = getOnboardingStage(stages, stageName);
  return stage ? stage.status === ONBOARDING_STAGE_STATUS.skipped : false;
};

export const isOnboardingStageDone = (
  stages: OnboardingStage[],
  stageName: OnboardingStageName
): boolean =>
  isOnboardingStageFinished(stages, stageName) || isOnboardingStageSkipped(stages, stageName);

export const getOnboardingStageValue = (
  stages: OnboardingStage[],
  stageName: OnboardingStageName,
  defaultValue?: OnboardingStageValue
): OnboardingStageValue => {
  const stage = getOnboardingStage(stages, stageName);
  return stage ? stage.value : defaultValue;
};

export const isActiveOnboardingStage = (
  activeStage: OnboardingStageName,
  onboardingStage: OnboardingStageName
): boolean => {
  return activeStage?.toLowerCase() === onboardingStage.toLowerCase();
};
